'use client';

import { useTranslation } from 'react-i18next';
import { SvgInfo } from '@/components/icons-eataly';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDateForLocale } from '@/lib/date-utils';
import type { OmnibusPriceDisplay } from '@/lib/omnibus';
import { cn } from '@/lib/utils';

export interface OmnibusPriceIndicationProps {
    /** `omnibus` value of `useProductPriceWeightDisplay`; nothing is rendered when null. */
    indication: OmnibusPriceDisplay | null | undefined;
    className?: string;
    invertTextColor?: boolean;
}

/**
 * EU Omnibus directive price disclosure (EATWS-472): the "Prezzo precedente" label with its legal
 * tooltip, the struck-through previous total and per-unit prices (lowest price of the 30 days before
 * the promotion started) and the promotion period. The data comes from the `c_priorBestPrice` /
 * `c_priceValid*` attributes injected by the SCAPI hooks (`omnibusEnabled` site preference), so
 * nothing renders when the feature is off or the product is not reduced.
 */
export function OmnibusPriceIndication({ indication, className, invertTextColor }: OmnibusPriceIndicationProps) {
    const { t, i18n } = useTranslation('product');
    if (!indication) return null;

    const locale = i18n.language;
    const from = formatDateForLocale(indication.validFrom ?? undefined, locale);
    const to = formatDateForLocale(indication.validTo ?? undefined, locale);
    const validity =
        from && to
            ? t('price.omnibus.validityRange', { from, to })
            : from
              ? t('price.omnibus.validityFrom', { from })
              : to
                ? t('price.omnibus.validityTo', { to })
                : null;

    if (!indication.priorFormatted && !validity) return null;

    const primaryColor = invertTextColor ? 'text-neutral-white' : 'text-neutral-10';
    const secondaryColor = invertTextColor ? 'text-neutral-gray-70' : 'text-neutral-40';

    return (
        <div
            className={cn('flex w-full min-w-0 flex-col gap-0.5 body-copy-s', primaryColor, className)}
            data-testid="omnibus-price-indication">
            {indication.priorFormatted ? (
                <div className="flex w-full min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
                    <span>{t('price.omnibus.previousPriceLabel')}</span>
                    <s className="body-copy-s-bold line-through" data-testid="omnibus-previous-price">
                        {indication.priorFormatted}
                    </s>
                    {indication.priorPerUnitLine ? (
                        <s className={secondaryColor} data-testid="omnibus-previous-unit-price">
                            {indication.priorPerUnitLine}
                        </s>
                    ) : null}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className="relative z-20 inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-full"
                                aria-label={t('price.omnibus.tooltipAriaLabel')}
                                data-testid="omnibus-tooltip-trigger">
                                <SvgInfo className="size-4" aria-hidden />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={6} className="max-w-xs px-3 py-2 text-left">
                            {t('price.omnibus.tooltip')}
                        </TooltipContent>
                    </Tooltip>
                </div>
            ) : null}
            {validity ? (
                <p className={cn('m-0', secondaryColor)} data-testid="omnibus-validity">
                    {validity}
                </p>
            ) : null}
        </div>
    );
}
